import { useFields } from '@hooks'
import { useEffect, useMemo } from 'react'
import { Column, SortingRule, useFlexLayout, useSortBy, useTable } from 'react-table'
import {
  Button,
  ButtonGroup,
  Card,
  Flexbox,
  Icon,
  Modal,
  Search,
  Select,
  Spacer,
  Table,
  Text,
} from '@components/common'
import { Segment } from '@api/models'
import Pagination from '@components/Pagination'
import { SegmentsHook } from '../useSegments'
import { SEGMENT_FORM_ID } from '@constants'
import SegmentForm from '../SegmentForm'
import SegmentsTableLoading from './SegmentsTableLoading'

const SegmentsTable = ({
  pageNumber,
  itemsPerPage,
  handleSearch,
  searchValue,
  changePage,
  sortByInitialState,
  updateSortSearchParam,
  //
  searchKey,
  isLoadingSegmentTypes,
  isErrorGettingSegmentTypes,
  segments,
  isLoadingSegments,
  isErrorFetchingSegments,
  isRefetchingSegments,
  selectSegmentTypeOptions,
  handleChangeSegmentType,
  selectSegmentTypeValue,

  handleDeleteSegment,
  isLoadingDeleteSegment,
  openDeleteSegmentModal,
  selectedSegment,
  deleteSegmentModalIsOpen,
  closeDeleteSegmentModal,
  openEditSegmentModal,
  closeEditSegmentModal,
  editSegmentModalIsOpen,
  handleEditSegment,
  isLoadingEditSegment,
  canEdit,
}: SegmentsHook) => {
  // Declare columns
  const { fields, isLoading: isLoadingFields, isError: isErrorGettingFields } = useFields()

  const columns = useMemo<Column<Segment>[]>(
    () => [
      {
        Header: fields.KY_VALUE,
        accessor: 'KY_VALUE',
      },
      {
        Header: fields.KY_DESC,
        accessor: 'KY_DESC',
      },
      {
        Header: '',
        accessor: 'CompanyStructure',
        disableSortBy: true,
        Cell: ({ row }) => (
          <Flexbox alignItems="center" justifyContent="flex-end" style={{ width: '100%' }}>
            <ButtonGroup hidden={!canEdit}>
              <Button
                icon={<Icon name="Edit" />}
                onClick={e => {
                  e.stopPropagation()
                  openEditSegmentModal(row.original)
                }}
                size="compact"
              >
                Edit
              </Button>

              <Button
                icon={<Icon name="Trash2" />}
                intent="danger"
                onClick={e => {
                  e.stopPropagation()
                  openDeleteSegmentModal(row.original)
                }}
                size="compact"
              >
                Delete
              </Button>
            </ButtonGroup>
          </Flexbox>
        ),
      },
    ],
    [isLoadingFields]
  )

  // Declare react table
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setSortBy,
    state: { sortBy },
  } = useTable(
    {
      columns,
      data: segments ? segments.list : [],
      manualSortBy: true,
      disableMultiSort: true,
      initialState: {
        sortBy: sortByInitialState() as SortingRule<Segment>[],
      },
    },
    useSortBy,
    useFlexLayout
  )

  // Update sort param
  useEffect(() => {
    updateSortSearchParam(sortBy, setSortBy)
  }, [sortBy])

  // Returns
  const isTableLoading = isLoadingSegmentTypes || isLoadingSegments || isLoadingFields
  const isError =
    isErrorGettingSegmentTypes || isErrorFetchingSegments || isErrorGettingFields

  if (isTableLoading) {
    return <SegmentsTableLoading />
  }

  if (isError) {
    return <Table.NoResults heading="There was an error." />
  }

  return (
    <>
      <Modal
        isOpen={deleteSegmentModalIsOpen}
        close={closeDeleteSegmentModal}
        title={`Delete ${selectedSegment?.KY_VALUE}_${selectedSegment?.KY_DESC}`}
        width={540}
        action={() =>
          handleDeleteSegment({
            KY_DESC: selectedSegment?.KY_DESC,
            KY_VALUE: selectedSegment?.KY_VALUE,
            KY_NR: selectedSegment?.KY_NR,
          })
        }
        actionLabel="Delete record"
        intent="danger"
        actionAutoFocus
        isActionLoading={isLoadingDeleteSegment}
      >
        <Text p>
          Are you sure you want to delete{' '}
          <strong>
            {selectedSegment?.KY_VALUE}_{selectedSegment?.KY_DESC}
          </strong>
          ? <Text intent="danger">This cannot be undone</Text>.
        </Text>
      </Modal>

      <Modal
        isOpen={editSegmentModalIsOpen}
        close={closeEditSegmentModal}
        title={`Edit description for segment ${selectedSegment?.KY_VALUE}`}
        width={540}
        actionLabel="Edit segment"
        form={SEGMENT_FORM_ID}
        isActionLoading={isLoadingEditSegment}
      >
        <SegmentForm
          isEdit={true}
          initialValues={{
            KY_DESC: selectedSegment?.KY_DESC,
            KY_VALUE: selectedSegment?.KY_VALUE,
            KY_NR: selectedSegment?.KY_NR,
          }}
          handleSubmit={handleEditSegment}
        />
      </Modal>

      <Card spacing="none">
        <Table.Actions>
          <Flexbox>
            <Select
              value={selectSegmentTypeValue}
              options={selectSegmentTypeOptions}
              onChange={handleChangeSegmentType}
              width="260px"
            />
            <Spacer x="mini" />
            <Search
              onChange={handleSearch}
              placeholder="Search by key description..."
              name={searchKey}
              width={'640px'}
              value={searchValue}
            />
          </Flexbox>
        </Table.Actions>
        {segments.list.length === 0 ? (
          <Table.NoResults text="" />
        ) : (
          <>
            <Table
              {...getTableProps()}
              aria-label="Company Table"
              label="Company Table"
              aria-colcount={columns.length}
              aria-rowcount={segments.list.length}
              height="calc(100vh - 280px)"
            >
              <Table.Head sticky>
                {isRefetchingSegments ? (
                  <Table.LoadingRows />
                ) : (
                  headerGroups.map((headerGroup, i) => (
                    <Table.HeadRow {...headerGroup.getHeaderGroupProps()} key={i}>
                      {headerGroup.headers.map((column, i) => (
                        <Table.Header
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                          column={column}
                          key={i}
                          tabIndex={0}
                        >
                          {column.render('Header')}
                        </Table.Header>
                      ))}
                    </Table.HeadRow>
                  ))
                )}
              </Table.Head>

              <Table.Body {...getTableBodyProps()}>
                {rows.map((row, i) => {
                  prepareRow(row)
                  return (
                    <Table.Row
                      {...row.getRowProps()}
                      onClick={() => {
                        if (!canEdit) return
                        openEditSegmentModal(row.original)
                      }}
                      tabIndex={0}
                      key={i}
                    >
                      {row.cells.map(cell => (
                        <Table.Cell {...cell.getCellProps()} column={cell.column}>
                          {cell.render('Cell')}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table>
            <Pagination
              changePage={changePage}
              totalItems={segments.count}
              maxItemsPerPage={itemsPerPage}
              currentPageNumber={pageNumber}
              label={`Showing ${segments.list.length} of ${segments.count} record${
                segments.count > 1 ? 's' : ''
              }`}
            />
          </>
        )}
      </Card>
    </>
  )
}
export default SegmentsTable
